import { ActivityFeed } from '@/components/ActivityFeed'
import { StatsCards } from '@/components/StatsCards'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { itemStatusConfig, type ItemStatus } from '@/constants/item'
import { useDateFormatter } from '@/hooks/useDateFormatter'
import { useUI } from '@/hooks/useUI'
import { AlertTriangle, Calendar } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useGetDashboard } from '../hooks/useDashboard'

const isKnownItemStatus = (status: string): status is ItemStatus => status in itemStatusConfig

export function DashboardView() {
  const { t } = useTranslation()
  const { data, isLoading, error } = useGetDashboard()
  const { itemStatuses, getEnumLabel } = useUI()
  const { formatDateTime } = useDateFormatter()
  const upcoming = data?.upcomingMaintenance ?? []
  const alerts = data?.attentionItems ?? []

  return (
    <div className="flex min-w-0 max-w-full flex-col gap-6 overflow-x-hidden">
      {error && (
        <div
          data-testid="dashboard-error"
          className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive"
        >
          {t('dashboard.loadError')}
        </div>
      )}

      <StatsCards summary={data?.summary} pendingBadges={data?.pendingBadges} />

      <div className="grid min-w-0 grid-cols-1 gap-6 lg:grid-cols-2">
        <ActivityFeed entries={data?.recentActivities} isLoading={isLoading} />

        <div className="flex flex-col gap-4">
          <Card className="border-border/50 bg-card" data-testid="dashboard-upcoming-maintenance">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-sm font-medium text-foreground">
                <Calendar className="size-4 text-primary" />
                {t('dashboard.upcomingMaintenance')}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="flex flex-col gap-2">
                {isLoading && <p className="py-4 text-center text-xs text-muted-foreground">{t('common.loading')}</p>}
                {!isLoading &&
                  upcoming.map(item => (
                    <div
                      key={item.itemId}
                      className="flex items-center justify-between rounded-lg border border-border/30 bg-secondary/20 p-3"
                    >
                      <div className="flex flex-col gap-0.5">
                        <div className="flex gap-1">
                          <span className="text-xs font-medium text-foreground">{item.productName}</span>
                          <span className="text-xs font-medium text-muted-foreground">({item.maintenanceLabel})</span>
                        </div>
                        <span className="text-[10px] text-muted-foreground">{item.serialNumber}</span>
                      </div>
                      <div className="flex flex-col gap-0.5">
                        <span className="text-xs text-warning">
                          {formatDateTime(item.nextMaintenance, {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                          })}
                        </span>
                        <span className="text-center text-xs text-muted-foreground">
                          {t('common.day', { count: Number(item.daysUntil) })}
                        </span>
                      </div>
                    </div>
                  ))}
                {!isLoading && upcoming.length === 0 && (
                  <p className="py-4 text-center text-xs text-muted-foreground">{t('dashboard.noItems')}</p>
                )}
              </div>
            </CardContent>
          </Card>

          {alerts.length > 0 && (
            <Card className="border-border/50 bg-card">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-sm font-medium text-foreground">
                  <AlertTriangle className="size-4 text-destructive" />
                  {t('dashboard.attentionItems', { count: alerts.length })}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="flex flex-col gap-2">
                  {alerts.slice(0, 6).map(item => {
                    const config = isKnownItemStatus(item.status) ? itemStatusConfig[item.status] : null

                    return (
                      <div
                        key={item.itemId}
                        className="flex items-center justify-between rounded-lg border border-border/30 bg-secondary/20 p-3"
                      >
                        <div className="flex flex-col gap-0.5 text-[12px]">
                          <span className="text-xs font-medium text-foreground">{item.productName}</span>
                          <span className="text-muted-foreground">{item.serialNumber}</span>
                        </div>
                        <div className="flex flex-col items-end gap-0.5 text-[12px]">
                          <span
                            className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 ${config?.bgColor ?? 'bg-muted/15'} ${config?.color ?? 'text-muted-foreground'}`}
                          >
                            <span className="size-1.5 rounded-full bg-current" />
                            {getEnumLabel(itemStatuses, item.status, item.status)}
                          </span>
                          <span className="text-muted-foreground">
                            {t('common.day', { count: Number(item.daysSince) })}
                          </span>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  )
}
